
import React, { useState, useEffect, useRef, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell, faTrash, faBars } from '@fortawesome/free-solid-svg-icons';
import { Client } from "@stomp/stompjs";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import './Navbar.css';
import companyLogo from '../../LOGO/zasya-logo.jpg';
import notificationSound from '../Sound/notify.mp3';
import { AppLayoutContext } from '../../App'; // Adjust path based on your folder structure

// Create the STOMP client instance ONCE, outside the component.
const stompClient = new Client({
    brokerURL: 'wss://swagger-dev.zasya.online/ws-notifications',
    reconnectDelay: 10000,
    debug: (str) => console.log(`[STOMP DEBUG] ${str}`),
});

const ZTaskLogo = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="#4F46E5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M2 17L12 22L22 17" stroke="#4F46E5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M2 12L12 17L22 12" stroke="#4F46E5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const Navbar = () => {
    const [notifications, setNotifications] = useState([]);
    const [isNotificationVisible, setNotificationVisible] = useState(false);
    const [hideTimeoutId, setHideTimeoutId] = useState(null);
    const subscriptionRef = useRef(null);
    const navigate = useNavigate();

    const audioRef = useRef(new Audio(notificationSound));
    const isAudioUnlocked = useRef(false);

    const { toggleSidebar } = useContext(AppLayoutContext);

    useEffect(() => {
        const unlockAudioContext = () => {
            if (!isAudioUnlocked.current) {
                const audio = audioRef.current;
                audio.muted = true;
                const playPromise = audio.play();

                if (playPromise !== undefined) {
                    playPromise.then(() => {
                        audio.pause();
                        audio.currentTime = 0;
                        audio.muted = false;
                        isAudioUnlocked.current = true;
                        console.log("✅ Audio context unlocked by user interaction.");
                        document.removeEventListener('click', unlockAudioContext);
                        document.removeEventListener('keydown', unlockAudioContext);
                    }).catch(error => {
                        console.error("Audio unlock failed on first attempt:", error);
                    });
                }
            }
        };
        
        document.addEventListener('click', unlockAudioContext);
        document.addEventListener('keydown', unlockAudioContext);

        return () => {
            document.removeEventListener('click', unlockAudioContext);
            document.removeEventListener('keydown', unlockAudioContext);
        };
    }, []);


    const getUserDetails = () => {
        const userDetailsString = localStorage.getItem('zTaskUserDetails');
        if (userDetailsString) {
            try { return JSON.parse(userDetailsString); }
            catch (error) { console.error("Error parsing user details:", error); return null; }
        }
        return null;
    };

    const currentUser = getUserDetails();
    const userId = currentUser ? currentUser.id : null;

    // WebSocket connection logic
    useEffect(() => {
        if (!userId) {
            return;
        }
        
        const onMessageReceived = (message) => {
            console.log('✅✅✅ REAL-TIME NOTIFICATION RECEIVED! ✅✅✅');
            console.log('[WS Real-Time] Raw message body:', message.body);
            
            try {
                const newNotification = JSON.parse(message.body);
                toast.info(newNotification.message, { position: "top-right", autoClose: 3000, theme: "light" });
                
                // Add a unique client-side ID for state management if the server doesn't provide one
                const notificationWithId = {
                    ...newNotification,
                    // Use server-provided notificationId or generate one for key prop
                    id: newNotification.notificationId || `client-${Date.now()}-${Math.random()}` 
                };

                setNotifications(prevNotifications => [notificationWithId, ...prevNotifications]);
                
                if (isAudioUnlocked.current) {
                    audioRef.current.play().catch(error => console.error("[Audio Error] Could not play sound:", error));
                } else {
                    console.warn("[Audio Info] Sound is disabled until first user interaction.");
                }

            } catch (error) {
                console.error("[WS Real-Time] Error processing WebSocket message:", error);
            }
        };

        stompClient.onConnect = () => {
            console.log('✅ [WS Effect] WebSocket Connected!');
            const subscriptionUrl = `/topic/notifications/${userId}`;
            console.log(`[WS Effect] Subscribing to: ${subscriptionUrl}`);
            
            if (subscriptionRef.current) {
                subscriptionRef.current.unsubscribe();
            }
            
            subscriptionRef.current = stompClient.subscribe(subscriptionUrl, onMessageReceived);
        };

        stompClient.onWebSocketClose = (event) => {
            console.log('🔌 [WS Info] WebSocket Connection Closed.', event);
            subscriptionRef.current = null;
        };

        stompClient.onStompError = (frame) => console.error('❌ [WS Error] STOMP protocol error:', frame);
        stompClient.onWebSocketError = (error) => console.error('❌ [WS Error] WebSocket connection error:', error);
        
        stompClient.activate();

        return () => {
            if (subscriptionRef.current) {
                console.log("[WS Cleanup] Unsubscribing from notifications on component unmount.");
                subscriptionRef.current.unsubscribe();
                subscriptionRef.current = null;
            }
        };
    }, [userId]);

    const handleNotificationClick = (taskId) => {
        if (!taskId) {
            console.warn("Notification clicked, but no taskId was provided.");
            return;
        }
        
        navigate('/tasks', { 
            state: { 
                initialFilter: { 
                    taskId: String(taskId)
                } 
            } 
        });
        
        setNotificationVisible(false);
    };

    // --- MODIFIED: Handles marking as seen only on the frontend ---
    const markAsSeen = (notificationId, event) => {
        event.stopPropagation();
        setNotifications(prev => 
            prev.map(notif => 
                notif.id === notificationId ? { ...notif, seen: true } : notif
            )
        );
    };

    // --- MODIFIED: Handles clearing a single notification only on the frontend ---
    const clearNotification = (notificationId, event) => {
        event.stopPropagation();
        setNotifications(prev => 
            prev.filter(n => n.id !== notificationId)
        );
    };

    // --- MODIFIED: Handles clearing all notifications only on the frontend ---
    const clearAllNotifications = () => {
        setNotifications([]);
    };

    const handleMouseEnter = () => {
        if (hideTimeoutId) clearTimeout(hideTimeoutId);
        setNotificationVisible(true);
    };

    const handleMouseLeave = () => {
        const timeoutId = setTimeout(() => { setNotificationVisible(false); }, 300);
        setHideTimeoutId(timeoutId);
    };

    const unreadCount = notifications.filter(n => !n.seen).length;

    return (
        <>
            <ToastContainer />
            <nav className="navbar-container">
                 <div className="navbar-left">
                     <button className="navbar-icon-button hamburger-menu" onClick={toggleSidebar}>
                         <FontAwesomeIcon icon={faBars} />
                     </button>
                     <Link to="/" className="navbar-logo-link">
                         <ZTaskLogo />
                         <span className="navbar-brand-name">ZTask</span>
                     </Link>
                 </div>
                 {userId && (
                     <div className="navbar-right">
                         <div
                             className="notification-wrapper"
                             onMouseEnter={handleMouseEnter}
                             onMouseLeave={handleMouseLeave}
                         >
                             <button className="navbar-icon-button">
                                 <FontAwesomeIcon icon={faBell} />
                                 {unreadCount > 0 && <span className="notification-badge">{unreadCount}</span>}
                             </button>
                             {isNotificationVisible && (
                                 <div className="notification-panel">
                                     <div className="notification-header">
                                         <h3>Notifications</h3>
                                         {notifications.length > 0 && (
                                             <button onClick={clearAllNotifications} className="clear-all-btn">
                                                 Clear All
                                             </button>
                                         )}
                                     </div>
                                     <div className="notification-list">
                                         {notifications.length > 0 ? (
                                             notifications.map((notif) => (
                                                 <div
                                                     key={notif.id} // Use the unique client-side ID for the key
                                                     className={`notification-item ${notif.seen ? 'notification-item--seen' : ''} notification-item--clickable`}
                                                     onClick={() => handleNotificationClick(notif.taskId)}
                                                 >
                                                     <div className="notification-content">
                                                         <p>{notif.message}</p>
                                                         <span className="notification-timestamp">
                                                             {new Date(notif.timeStamp).toLocaleString()}
                                                         </span>
                                                     </div>
                                                     <div className="notification-actions">
                                                         {!notif.seen && (
                                                             <button onClick={(e) => markAsSeen(notif.id, e)} className="mark-as-read-btn">
                                                                 Mark as Read
                                                             </button>
                                                         )}
                                                         <button onClick={(e) => clearNotification(notif.id, e)} className="delete-notification-btn">
                                                             <FontAwesomeIcon icon={faTrash} />
                                                         </button>
                                                     </div>
                                                 </div>
                                             ))
                                         ) : (
                                             <div className="notification-item-empty"><p>No new notifications</p></div>
                                         )}
                                     </div>
                                 </div>
                             )}
                         </div>
                         <a href="https://zasyaonline.com/" target="_blank" rel="noopener noreferrer" className="navbar-icon-button navbar-user-profile" aria-label="Company Website">
                            <img src={companyLogo} alt="Company Logo" className="navbar-company-logo" />
                         </a>
                     </div>
                 )}
             </nav>
        </>
    );
};

export default Navbar;